import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import useConversationScroll from "@/lib/useConversationScroll";
import { Send, Loader2, Sparkles, BrainCircuit, TrendingUp, Paperclip, Eraser } from "lucide-react";
import { API, formatApiError, useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import AssistantActions from "@/components/dashboard/AssistantActions";
import VoiceButton from "@/components/dashboard/VoiceButton";
import AISeesIndicator from "@/components/dashboard/AISeesIndicator";
import AttachmentChip from "@/components/dashboard/AttachmentChip";
import MarkdownMessage from "@/components/dashboard/MarkdownMessage";
import { streamAssistantChat } from "@/lib/aiStream";
import { confirmAction, cancelAction } from "@/lib/aiActions";
import ProposedActionCard from "@/components/dashboard/ProposedActionCard";
import RecoveredActionCard from "@/components/dashboard/RecoveredActionCard";
import {
  uploadAiFile,
  deleteAiFile,
  validateUpload,
  AI_UPLOAD_ACCEPT_ATTR,
} from "@/lib/aiUpload";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Phase 4D · formatter for `kind:"read_result"` payloads from the
// NL action bridge. Plain language, never raw JSON. Backend already
// stripped sensitive fields in orchestrator._strip_lead/_strip_invoice.
function renderReadResult(result, actionId) {
  if (!result || typeof result !== "object") return null;
  if (actionId === "projects.read") {
    const projs = result.projects || [];
    if (projs.length === 0) return <div className="text-neutral-500">No projects yet.</div>;
    return (
      <ul className="list-disc list-inside space-y-0.5">
        {projs.map((p) => (
          <li key={p.id} data-testid={`nl-project-${p.id}`}>
            <span className="font-medium">{p.name}</span>
            {p.status ? <span className="text-neutral-500"> · {p.status}</span> : null}
          </li>
        ))}
      </ul>
    );
  }
  if (actionId === "finance.invoice.read") {
    const invs = result.invoices || [];
    if (invs.length === 0) return <div className="text-neutral-500">No invoices yet.</div>;
    return (
      <ul className="space-y-0.5">
        {invs.map((inv) => (
          <li key={inv.id} data-testid={`nl-invoice-${inv.id}`} className="flex justify-between gap-2">
            <span>
              <span className="font-medium">{inv.number}</span>
              <span className="text-neutral-500"> · {inv.client_name}</span>
            </span>
            <span className="text-neutral-700">
              {inv.currency || "EUR"} {Number(inv.total || 0).toFixed(2)}
              <span className="ml-2 text-neutral-500">{inv.status}</span>
            </span>
          </li>
        ))}
      </ul>
    );
  }
  if (actionId === "sales.lead.read") {
    const leads = result.leads || [];
    if (leads.length === 0) return <div className="text-neutral-500">No leads yet.</div>;
    return (
      <ul className="list-disc list-inside space-y-0.5">
        {leads.map((l) => (
          <li key={l.id} data-testid={`nl-lead-${l.id}`}>
            <span className="font-medium">{l.name}</span>
            {l.company ? <span className="text-neutral-500"> · {l.company}</span> : null}
            {l.stage ? <span className="text-neutral-500"> · {l.stage}</span> : null}
          </li>
        ))}
      </ul>
    );
  }
  return <div className="text-neutral-500">Done.</div>;
}

const CONFIGS = {
  zyntha: {
    name: "Zyntha",
    specialty: "Content & SEO Specialist",
    description: "Creative, energetic, inspiring. Ask Zyntha for blog posts, captions, ad copy and SEO ideas.",
    color: "#8B5CF6",
    bg: "linear-gradient(135deg,#8B5CF6 0%,#6D28D9 100%)",
    icon: Sparkles,
    avatar: "/assistants/zyntha.png",
    starters: [
      "Write 5 LinkedIn captions for our launch",
      "Draft an SEO blog post on AI ERP for SMEs",
      "Repurpose this idea across 4 formats",
    ],
  },
  thoro: {
    name: "Thoro",
    specialty: "Builder & Workflow Specialist",
    description: "Technical, precise, results-driven. Use Thoro to design automations, SOPs and funnels.",
    color: "#06B6D4",
    bg: "linear-gradient(135deg,#06B6D4 0%,#0E7490 100%)",
    icon: BrainCircuit,
    avatar: "/assistants/thoro.png",
    starters: [
      "Build a customer onboarding workflow",
      "Write an SOP for invoice approval",
      "Plan a 3-step product funnel",
    ],
  },
  zyona: {
    name: "Zyona",
    specialty: "Business & Growth Specialist",
    description: "Strategic, business-focused, decisive. Talk to Zyona about growth, pricing and KPIs.",
    color: "#D4AF37",
    bg: "linear-gradient(135deg,#D4AF37 0%,#8a6e1d 100%)",
    icon: TrendingUp,
    avatar: "/assistants/zyona.png",
    starters: [
      "Outline a 90-day growth plan",
      "Build a sales script for first calls",
      "Which 3 KPIs should I track weekly?",
    ],
  },
};

export default function AssistantPage({ assistantKey }) {
  const cfg = CONFIGS[assistantKey];
  // Hard guard: an unknown assistantKey must never crash the dashboard.
  // Render a safe inline fallback so the sidebar + topbar stay usable.
  if (!cfg) {
    return (
      <div className="rounded-xl border border-[#eef1f7] bg-white p-8" data-testid="assistant-unknown">
        <h2 className="text-[20px] font-bold text-[#0A1628]">Assistant not available</h2>
        <p className="text-[14px] text-[#4a5568] mt-2">
          This assistant isn&apos;t configured. Try one of Zyntha, Thoro or Zyona from the sidebar.
        </p>
      </div>
    );
  }
  return <ConfiguredAssistantPage assistantKey={assistantKey} cfg={cfg} />;
}

function ConfiguredAssistantPage({ assistantKey, cfg }) {
  const Icon = cfg.icon;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const [historyAttempt, setHistoryAttempt] = useState(0);
  const [poweredBy, setPoweredBy] = useState(null);
  // Pending attachments — cleared after successful send.
  // Shape: { local_id, file_id?, filename, size, status: "uploading"|"ready"|"error" }
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const conversationActionRef = useRef(null);
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const conversationScroll = useConversationScroll(scrollRef, contentRef, messages, true, assistantKey);
  const { user, activeWorkspace } = useAuth();
  const navigate = useNavigate();
  const isFounder = !!user?.is_founder;
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wiping, setWiping] = useState(false);

  const wipeMemory = async () => {
    if (conversationActionRef.current || attachments.some((a) => a.status === "uploading")) return;
    conversationActionRef.current = "wipe";
    setWiping(true);
    try {
      const { data } = await axios.delete(`${API}/ai/memory/${assistantKey}`, {
        headers: { "X-Zynthoro-Expected-Workspace": activeWorkspace || user?.id },
      });
      // Reset local chat state so the next message opens a fresh session.
      setSessionId(null);
      conversationScroll.scrollToLatest();
      setMessages([{
        role: "assistant",
        content: `Hi! I'm ${cfg.name}, your ${cfg.specialty.toLowerCase()}. What are we working on today?`,
      }]);
      setInput("");
      setAttachments([]);
      toast.success(
        `Memory cleared — ${data.messages_deleted} message${data.messages_deleted === 1 ? "" : "s"} wiped.`
      );
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't clear memory.");
    }
    conversationActionRef.current = null;
    setWiping(false);
    setWipeOpen(false);
  };

  useEffect(() => {
    // Reset the initial-scroll latch whenever we switch assistants —
    // the new conversation needs its own "stick to bottom" pass.
    conversationScroll.scrollToLatest();
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError("");
    (async () => {
      // Resume the user's most recent session with this assistant
      let resumed = false;
      try {
        const { data } = await axios.get(`${API}/ai/sessions`, {
          params: { assistant: assistantKey, limit: 1 },
          headers: { "X-Zynthoro-Expected-Workspace": activeWorkspace || user?.id },
        });
        if (cancelled) return;
        if (!Array.isArray(data?.sessions)) throw new Error("Incomplete session list.");
        const last = data.sessions[0];
        if (last && (typeof last.session_id !== "string" || !last.session_id)) throw new Error("Incomplete session.");
        if (last && !cancelled) {
          const hist = await axios.get(`${API}/ai/history`, {
            params: { session_id: last.session_id },
            headers: { "X-Zynthoro-Expected-Workspace": activeWorkspace || user?.id },
          });
          if (cancelled) return;
          if (!Array.isArray(hist.data?.messages) || hist.data.messages.some((m) =>
            !m || !["user", "assistant"].includes(m.role) || typeof m.content !== "string")) {
            throw new Error("Incomplete conversation history.");
          }
          const msgs = hist.data.messages.map((m) => ({
            role: m.role, content: m.content,
            actionReference: m.role === "assistant" && m.action_ref &&
              typeof m.action_ref.action_row_id === "string" &&
              typeof m.action_ref.action_id === "string" ? m.action_ref : null,
            originWorkspace: activeWorkspace || user?.id,
            originActor: user?.id,
          }));
          if (msgs.length) {
            conversationScroll.scrollToLatest();
            setSessionId(last.session_id);
            setMessages(msgs);
            resumed = true;
          }
        }
      } catch {
        if (!cancelled) setHistoryError("Your previous conversation could not be loaded. Retry before sending another message.");
        return;
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
      if (!resumed && !cancelled) {
        setMessages([{
          role: "assistant",
          content: `Hi! I'm ${cfg.name}, your ${cfg.specialty.toLowerCase()}. What are we working on today?`,
        }]);
        setSessionId(null);
      }
    })();
    return () => { cancelled = true; };
  }, [assistantKey, activeWorkspace, user?.id, historyAttempt]);

  const send = async (text) => {
    const value = (text ?? input).trim();
    if (!value || busy || historyLoading || historyError || conversationActionRef.current) return;
    // Wait for any in-flight upload — do not send a message with a
    // half-uploaded file, the server won't know about it yet.
    if (attachments.some((a) => a.status === "uploading")) {
      toast.info("Please wait for the file upload to finish.");
      return;
    }
    conversationActionRef.current = "send";
    if (!text) setInput("");

    const readyAttachments = attachments.filter((a) => a.status === "ready" && a.file_id);
    const fileIds = readyAttachments.map((a) => a.file_id);
    // Keep selected files until a successful response; failed requests retain them.
    const bubbleAttachments = readyAttachments.map((a) => ({
      file_id: a.file_id,
      filename: a.filename,
      size: a.size,
    }));
    const clearSentAttachments = () => {
      const sentIds = new Set(readyAttachments.map((a) => a.local_id));
      setAttachments((current) => current.filter((a) => !sentIds.has(a.local_id)));
    };
    const restoreFailedInput = () => {
      if (!text) setInput((current) => current || value);
    };

    // Append the user message (with attachment metadata), then an empty assistant message
    setMessages((m) => [
      ...m,
      { role: "user", content: value, attachments: bubbleAttachments },
      { role: "assistant", content: "", streaming: true },
    ]);
    setBusy(true);

    let localSession = sessionId;
    let hadError = false;

    // Phase 4D/4E · All four assistants route through the shared NL
    // action bridge so each one can reach its approved catalogue entries.
    // Per-assistant whitelists are enforced server-side in registry.py.
    // When the backend gate is off (503 PHASE4_DISABLED), we auto-
    // degrade to the locked streaming path so the UX never breaks.
    const NL_ASSISTANTS = ["zynthoro_assist", "zyntha", "thoro", "zyona"];
    let phase4Disabled = false;
    if (NL_ASSISTANTS.includes(assistantKey)) {
      try {
        const { data } = await axios.post(`${API}/ai/actions/nl`, {
          assistant_key: assistantKey,
          message: value,
          file_ids: fileIds.length ? fileIds : undefined,
          // Regression repair 2026-10-03 · Issue 2: pass the current
          // session_id so the NL bridge loads prior history and
          // persists the new turn into the same conversation. On the
          // first turn `sessionId` is still null and the backend mints
          // one, echoed back in `data.session_id`.
          session_id: sessionId || undefined,
        }, {
          headers: { "X-Zynthoro-Expected-Workspace": activeWorkspace || user?.id },
        });
        // Capture the backend-minted session_id on the very first turn.
        if (data?.session_id && !sessionId) {
          localSession = data.session_id;
          setSessionId(data.session_id);
        }
        if (data.kind === "error") hadError = true;
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (!last || last.role !== "assistant") return next;
          if (data.kind === "prose") {
            next[next.length - 1] = {
              role: "assistant",
              content: data.message || "",
            };
          } else if (data.kind === "clarification") {
            next[next.length - 1] = {
              role: "assistant",
              content: data.message || "I need a bit more detail.",
              clarification: true,
            };
          } else if (data.kind === "read_result") {
            next[next.length - 1] = {
              role: "assistant",
              content: "",
              readResult: data.result,
              actionId: data.action_id,
            };
          } else if (data.kind === "proposal") {
            next[next.length - 1] = {
              role: "assistant",
              content: "Here's what I'll do — review and approve to run it.",
              proposal: data.proposal,
              proposalWorkspace: activeWorkspace || user?.id,
              proposalActor: user?.id,
              actionId: data.action_id,
            };
          } else if (data.kind === "error") {
            next[next.length - 1] = {
              role: "assistant",
              content: data.code === "AI_RESPONSE_INCOMPLETE"
                ? "The answer reached its output limit and was not completed. Please request one section at a time. No action was executed."
                : "I can't do that from here. Try rephrasing or ask Zynthoro Assist for help.",
            };
          } else {
            next[next.length - 1] = {
              role: "assistant",
              content: "I didn't catch that. Could you rephrase?",
            };
          }
          return next;
        });
      } catch (e) {
        hadError = true;
        const detail = e?.response?.data?.detail;
        const code = typeof detail === "object" ? detail?.code : detail;
        // Phase 4E · 503 PHASE4_DISABLED → silently fall back to the
        // locked streaming path. The user never sees a disabled window.
        if (e?.response?.status === 503 && code === "PHASE4_DISABLED") {
          phase4Disabled = true;
          hadError = false;
        } else if (code === "AI_CREDIT_LIMIT") {
          toast.error(detail?.message || "AI credit limit reached.", {
            action: { label: "Buy credits", onClick: () => navigate("/dashboard/credits") },
            duration: 8000,
          });
        } else if (code === "RATE_LIMITED") {
          toast.error("You've reached the hourly limit on AI actions. Please wait and try again.");
        } else {
          toast.error(formatApiError(code || "") || `${cfg.name} is unavailable.`);
        }
        if (!phase4Disabled) {
          setMessages((m) => {
            const next = [...m];
            const last = next[next.length - 1];
            if (last && last.role === "assistant" && !last.content) {
              next[next.length - 1] = {
                role: "assistant",
                content:
                  code === "AI_CREDIT_LIMIT"
                    ? "You've used all available AI credits. Tap **Buy credits** in the sidebar to top up."
                    : "I had trouble responding. Please try again shortly.",
                creditBlocked: code === "AI_CREDIT_LIMIT",
              };
            }
            return next;
          });
        }
      }
      if (!phase4Disabled) {
        if (hadError) restoreFailedInput();
        else clearSentAttachments();
        conversationActionRef.current = null;
      setBusy(false);
        return;
      }
    }

    try {
    await streamAssistantChat({
      expectedWorkspace: activeWorkspace || user?.id,
      assistant: assistantKey,
      session_id: sessionId || undefined,
      message: value,
      file_ids: fileIds.length ? fileIds : undefined,
      onMeta: (meta) => {
        if (meta?.session_id && !localSession) {
          localSession = meta.session_id;
          setSessionId(meta.session_id);
        }
        if (meta?.badge) {
          setPoweredBy({ badge: meta.badge, provider: meta.provider, model: meta.model });
        }
      },
      onDelta: (d) => {
        const chunk = d?.content || "";
        if (!chunk) return;
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "assistant") {
            next[next.length - 1] = { ...last, content: (last.content || "") + chunk, streaming: true };
          }
          return next;
        });
      },
      onError: (err) => {
        hadError = true;
        if (err?.code === "AI_CREDIT_LIMIT") {
          toast.error(err?.message || "AI credit limit reached. Buy more to keep working.", {
            action: {
              label: "Buy credits",
              onClick: () => navigate(err?.buy_credits_url || "/dashboard/credits"),
            },
            duration: 8000,
          });
        } else {
          toast.error(formatApiError(err?.message) || `${cfg.name} is unavailable.`);
        }
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "assistant" && !last.content) {
            const isCreditErr = err?.code === "AI_CREDIT_LIMIT";
            next[next.length - 1] = {
              role: "assistant",
              content: isCreditErr
                ? "You've used all available AI credits. Tap **Buy credits** in the sidebar to top up."
                : "I had trouble responding. Please try again shortly.",
              creditBlocked: isCreditErr,
            };
          }
          return next;
        });
      },
      onDone: () => {
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          if (last && last.role === "assistant") {
            next[next.length - 1] = { ...last, streaming: false };
          }
          return next;
        });
      },
    });

    } catch {
      hadError = true;
      toast.error("The response was interrupted. Your message and attachments have been kept.");
    } finally {
      setMessages((current) => {
        const next = [...current];
        const last = next[next.length - 1];
        if (last?.role === "assistant") {
          next[next.length - 1] = {
            ...last,
            streaming: false,
            content: last.content || (hadError ? "The response was interrupted. Please review before retrying." : ""),
          };
        }
        return next;
      });
      conversationActionRef.current = null;
      setBusy(false);
    }

    if (hadError) restoreFailedInput();
    else clearSentAttachments();
    conversationActionRef.current = null;
      setBusy(false);
    if (hadError && !localSession) setSessionId(null);
  };

  // ---- Attachment handlers -------------------------------------------------
  const handleAttachClick = () => {
    if (busy || conversationActionRef.current) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    // Reset the input immediately so selecting the same file twice still fires change.
    e.target.value = "";
    if (busy || conversationActionRef.current || attachments.some((a) => a.status === "uploading")) return;
    if (attachments.length + files.length > 6) {
      toast.error("Select up to six attachments per message.");
      return;
    }
    for (const file of files) {
      const err = validateUpload(file);
      if (err) {
        toast.error(err);
        continue;
      }
      const local_id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setAttachments((prev) => [
        ...prev,
        { local_id, filename: file.name, size: file.size, status: "uploading" },
      ]);
      try {
        const res = await uploadAiFile(file, activeWorkspace || user?.id);
        setAttachments((prev) =>
          prev.map((a) =>
            a.local_id === local_id
              ? { ...a, file_id: res.file_id, size: res.size, status: "ready" }
              : a
          )
        );
      } catch (uploadErr) {
        const msg = formatApiError(uploadErr?.response?.data?.detail) || uploadErr?.message || "Upload failed.";
        toast.error(msg);
        setAttachments((prev) => prev.filter((a) => a.local_id !== local_id));
      }
    }
  };

  const removeAttachment = async (local_id) => {
    if (busy || conversationActionRef.current) return;
    const target = attachments.find((a) => a.local_id === local_id);
    setAttachments((prev) => prev.filter((a) => a.local_id !== local_id));
    if (target?.file_id) {
      // Best-effort server-side purge — TTL will handle it either way.
      deleteAiFile(target.file_id, activeWorkspace || user?.id).catch(() => {});
    }
  };

  return (
    <div data-testid={`assistant-page-${assistantKey}`} className="max-w-4xl">
      <div className="flex items-start gap-4">
        <span
          className="w-14 h-14 rounded-2xl inline-flex items-center justify-center shrink-0 text-white shadow-md"
          style={{ background: cfg.bg }}
        >
          <Icon size={22} />
        </span>
        <div className="flex-1">
          <p className="zy-eyebrow mb-1.5" style={{ color: cfg.color }}>AI Assistant</p>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-[28px] font-bold tracking-tight">{cfg.name}</h1>
            {poweredBy && (
              <span
                data-testid={`${assistantKey}-powered-by`}
                className="text-[10.5px] font-bold tracking-[0.12em] uppercase px-2.5 py-1 rounded-full"
                style={
                  poweredBy.provider === "gemini"
                    ? { background: "rgba(139,92,246,0.12)", color: "#6D28D9" }
                    : { background: "rgba(32,43,96,0.10)", color: "#202B60" }
                }
                title={poweredBy.model}
              >
                {poweredBy.badge}
              </span>
            )}
          </div>
          <p className="text-[14.5px] text-[#555] mt-1">{cfg.description}</p>
          <AISeesIndicator className="mt-1.5" testId={`${assistantKey}-ai-sees`} />
        </div>
        <button
          type="button"
          onClick={() => setWipeOpen(true)}
          disabled={wiping || busy}
          title="Wipe this assistant's stored memory for your workspace"
          data-testid={`${assistantKey}-clear-memory-btn`}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#0A162820] px-3 py-1.5 text-[12.5px] font-semibold text-[#0A1628] hover:bg-[#F7F8FA] transition-colors disabled:opacity-50"
        >
          {wiping ? <Loader2 size={13} className="animate-spin" /> : <Eraser size={13} />}
          Clear memory
        </button>
      </div>

      <div className="mt-7 bg-white border border-[#eee] rounded-2xl flex flex-col" style={{ height: "clamp(480px, 70dvh, 800px)" }}>
        {historyLoading && <p role="status" className="px-5 py-2 text-sm text-neutral-600">Loading your previous conversation…</p>}
        {historyError && <div role="alert" className="px-5 py-2 text-sm text-amber-800">
          <p>{historyError}</p>
          <button type="button" onClick={() => setHistoryAttempt((value) => value + 1)}
            className="mt-1 underline" disabled={historyLoading}>Retry conversation</button>
        </div>}
        <div ref={scrollRef} onScroll={conversationScroll.onScroll} onWheel={conversationScroll.onWheel} className="min-h-0 flex-1 overflow-y-auto p-5" data-testid={`${assistantKey}-messages`}>
          <div ref={contentRef} className="space-y-4">
          {messages.map((m, i) => (
            m.role === "user" ? (
              <div
                key={i}
                className="max-w-[80%] ml-auto flex flex-col items-end gap-1.5"
              >
                {m.attachments && m.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 justify-end" data-testid={`${assistantKey}-msg-${i}-attachments`}>
                    {m.attachments.map((a) => (
                      <AttachmentChip
                        key={a.file_id}
                        filename={a.filename}
                        size={a.size}
                        compact
                        testId={`${assistantKey}-msg-${i}-attachment-${a.file_id}`}
                      />
                    ))}
                  </div>
                )}
                <div
                  className="text-[14px] leading-relaxed px-4 py-2.5 rounded-lg rounded-tr-sm text-white whitespace-pre-wrap"
                  style={{ background: "#202B60" }}
                >
                  {m.content}
                </div>
              </div>
            ) : (
              <div key={i} className="flex items-start gap-2.5 max-w-[88%]">
                <img
                  src={cfg.avatar}
                  alt={`${cfg.name} avatar`}
                  className="w-9 h-9 rounded-full object-cover shrink-0"
                  style={{ border: `2px solid ${cfg.color}`, objectPosition: "center top" }}
                  data-testid={`${assistantKey}-msg-avatar`}
                />
                <div className="flex flex-col min-w-0">
                  <div className="text-[14px] leading-relaxed px-4 py-2.5 rounded-lg rounded-tl-sm bg-[#F4F6FB]">
                    <MarkdownMessage>{m.content}</MarkdownMessage>
                    {m.streaming && (
                      <span
                        className="inline-block w-[7px] h-[14px] ml-0.5 align-middle animate-pulse"
                        style={{ background: cfg.color }}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  {m.readResult && (
                    <div
                      data-testid={`${assistantKey}-msg-${i}-read-result`}
                      className="mt-2 bg-white border border-[#eee] rounded-lg p-3 text-[13px]"
                    >
                      {renderReadResult(m.readResult, m.actionId)}
                    </div>
                  )}
                  {m.actionReference && (
                    <RecoveredActionCard
                      key={`${m.originActor}:${m.originWorkspace}:${m.actionReference.action_row_id}`}
                      reference={m.actionReference}
                      renderReadResult={renderReadResult}
                      originWorkspace={m.originWorkspace}
                      originActor={m.originActor}
                    />
                  )}
                  {m.proposal && (
                    <ProposedActionCard
                      key={`${m.proposalActor}:${m.proposalWorkspace}:${m.proposal.action_row_id}`}
                      proposal={m.proposal}
                      originWorkspace={m.proposalWorkspace}
                      originActor={m.proposalActor}
                      initialState={m.proposalState}
                      onDone={(finalState) => {
                        setMessages((all) => {
                          const next = [...all];
                          const idx = next.findIndex(
                            (x) => x.proposal?.action_row_id === m.proposal.action_row_id,
                          );
                          if (idx >= 0) {
                            next[idx] = { ...next[idx], proposalState: finalState };
                          }
                          return next;
                        });
                      }}
                    />
                  )}
                  {!m.streaming && m.content && m.content.length > 10 && (
                    <AssistantActions
                      content={m.content}
                      assistantName={cfg.name}
                      testIdPrefix={`${assistantKey}-msg-${i}`}
                    />
                  )}
                </div>
              </div>
            )
          ))}
          {busy && !(messages.length > 0 && messages[messages.length - 1].role === "assistant" && messages[messages.length - 1].streaming) && (
            <div className="flex items-center gap-2.5">
              <img
                src={cfg.avatar}
                alt=""
                className="w-9 h-9 rounded-full object-cover opacity-70"
                style={{ border: `2px solid ${cfg.color}`, objectPosition: "center top" }}
              />
              <div className="flex items-center gap-2 text-[#888] text-[12.5px] bg-[#F4F6FB] px-4 py-2.5 rounded-lg rounded-tl-sm">
                <Loader2 size={13} className="animate-spin" /> {cfg.name} is thinking…
              </div>
            </div>
          )}
          </div>
        </div>
        {conversationScroll.showLatest && (
          <button type="button" onClick={conversationScroll.scrollToLatest}
            className="mx-auto my-2 rounded-full border px-4 py-1.5 text-sm">
            Latest messages ↓
          </button>
        )}

        {messages.length <= 1 && (
          <div className="px-5 pb-3 flex flex-wrap gap-2">
            {cfg.starters.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="text-[12.5px] px-3 py-1.5 rounded-full border border-[#eee] hover:border-[#202B60] hover:text-[#202B60]"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="p-3 border-t border-[#eee] flex flex-col gap-2"
        >
          {attachments.length > 0 && (
            <div
              className="flex flex-wrap gap-1.5"
              data-testid={`${assistantKey}-pending-attachments`}
            >
              {attachments.map((a) => (
                <AttachmentChip
                  key={a.local_id}
                  filename={a.filename}
                  size={a.size}
                  status={a.status}
                  onRemove={() => removeAttachment(a.local_id)}
                  testId={`${assistantKey}-pending-${a.local_id}`}
                />
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={AI_UPLOAD_ACCEPT_ATTR}
              multiple
              onChange={handleFileSelected}
              className="hidden"
              data-testid={`${assistantKey}-file-input`}
            />
            <button
              type="button"
              onClick={handleAttachClick}
              disabled={busy || wiping}
              title="Attach a file (PDF, DOCX, XLSX, PPTX, CSV — up to 10 MB)"
              aria-label="Attach a file"
              className="shrink-0 h-[44px] w-[44px] inline-flex items-center justify-center rounded-md border border-[#eee] text-[#555] hover:border-[#202B60] hover:text-[#202B60] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`${assistantKey}-attach-btn`}
            >
              <Paperclip size={17} />
            </button>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                // Enter sends; Shift+Enter adds a newline.
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={`Message ${cfg.name}…  (Shift+Enter for newline)`}
              data-testid={`${assistantKey}-input`}
              rows={2}
              className="flex-1 text-[14px] leading-relaxed outline-none px-3 py-2.5 rounded-md border border-[#eee] focus:border-[#202B60] resize-y min-h-[52px] max-h-[240px] whitespace-pre-wrap break-words"
            />
            <VoiceButton
              testId={`${assistantKey}-voice-btn`}
              onInterim={(t) => setInput(t)}
              onFinal={(t) => { setInput(""); send(t); }}
            />
            <button type="submit" disabled={busy || wiping || historyLoading || !!historyError || !input.trim()} className="zy-btn-primary px-3.5 py-2.5 disabled:opacity-50 shrink-0" data-testid={`${assistantKey}-send`}>
              <Send size={15} />
            </button>
          </div>
        </form>
      </div>

      <AlertDialog open={wipeOpen} onOpenChange={setWipeOpen}>
        <AlertDialogContent data-testid={`${assistantKey}-clear-memory-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear {cfg.name}&apos;s memory?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes every message {cfg.name} has stored for your workspace —
              past sessions, context, everything. Your next conversation will start from a clean slate.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${assistantKey}-clear-memory-cancel`} disabled={wiping || busy}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid={`${assistantKey}-clear-memory-confirm`}
              onClick={(e) => { e.preventDefault(); wipeMemory(); }}
              disabled={wiping || busy}
              className="bg-[#B42318] hover:bg-[#8B1A12]"
            >
              {wiping ? "Clearing…" : "Yes, clear memory"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

